import { useState, type FormEvent } from 'react';
import { toast } from 'react-hot-toast';
import { Loader2, Table2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Modal } from './Modal';
import type { Product } from '../mockKnowledge';

interface ConnectSheetModalProps {
  open: boolean;
  onClose: () => void;
  onConnect: (products: Product[]) => void;
}

const FAKE_SHEET_PRODUCTS: Array<Pick<Product, 'title' | 'price' | 'stock' | 'category'>> = [
  { title: 'Cotton Baby Romper (0-12m)', price: 420, stock: 20, category: 'Kids' },
  { title: 'Handwoven Jute Basket', price: 350, stock: 14, category: 'Home & Kitchen' },
  { title: 'Printed Cotton Panjabi — Grey', price: 1250, stock: 8, category: "Men's Fashion" },
  { title: 'Beaded Statement Necklace', price: 590, stock: 16, category: 'Accessories' },
];

/**
 * Connects a Google Sheet as a product source. No real Sheets API
 * integration exists yet — "Connect" simulates a pull (loading state, then
 * a fixed set of mock rows) rather than parsing an actual sheet.
 */
export function ConnectSheetModal({ open, onClose, onConnect }: ConnectSheetModalProps) {
  const [url, setUrl] = useState('');
  const [connecting, setConnecting] = useState(false);

  const handleClose = () => {
    setUrl('');
    setConnecting(false);
    onClose();
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!url.trim() || connecting) return;

    setConnecting(true);
    window.setTimeout(() => {
      const products: Product[] = FAKE_SHEET_PRODUCTS.map((item, index) => ({
        id: `sheet-${Date.now()}-${index}`,
        description: '',
        imageUrl: '/mock/placeholder.jpg',
        tags: ['sheet-import'],
        source: 'sheet',
        inKnowledgeBase: true,
        ...item,
      }));

      onConnect(products);
      toast.success(`Connected — imported ${products.length} products from your sheet`);
      handleClose();
    }, 1400);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Connect Google Sheet"
      description="Keep managing your catalog in a shared spreadsheet — we'll pull in products from it."
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <Label htmlFor="sheet-url">Google Sheet URL</Label>
          <Input
            id="sheet-url"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://docs.google.com/spreadsheets/d/…"
            required
            className="mt-1"
            disabled={connecting}
          />
          <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Table2 className="h-3 w-3 shrink-0" />
            Make sure the sheet is shared as "Anyone with the link — Viewer".
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={handleClose} disabled={connecting}>
            Cancel
          </Button>
          <Button type="submit" disabled={connecting} className="gap-1.5">
            {connecting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {connecting ? 'Connecting…' : 'Connect'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default ConnectSheetModal;
